/**
 * ORDER 44 — THE APP-SIDE CVD CURSOR CONTRACT.
 *
 * ⛔ WHY THIS IS A SECOND MODULE AND NOT A WIDENING OF `candlePaging.ts`. The two rails page the
 * SAME axis and they do NOT page the same way, and pretending otherwise is the bug that would
 * ship: candles page only at `tf >= 3` (their deep door is aggregated-ES-only and 400s below
 * that), CVD pages at EVERY minute rung because its table IS the one-minute spine. One shared
 * `canPage*` predicate would either lock CVD out of 1m — the rung the owner lives on — or send
 * candles a cursor their route refuses. ⇒ ***TWO ROUTES WITH DIFFERENT REFUSALS NEED TWO
 * READERS; A SHARED ONE IS ONLY AS TRUE AS THE STRICTER ROUTE.***
 *
 * ⛔ AND THE PREPEND IS A MERGE, NOT A CONCATENATION. `mergeCvdPages` is keyed on `t` with the
 * CURRENT page winning, so the live forming bar can never be displaced by a history page that
 * happens to overlap it. That direction is deliberate and is the same one `mergeCandlePages`
 * chose one file over: history is older and colder; the live poll is the authority on any `t`
 * they both carry.
 */

import { rungKey, rungLabel, rungSpanMs, type Rung } from "./rungLadder.ts";
import type { Cvd2Bar, Cvd2Feed } from "./cvd2.ts";

/** The Worker's declared per-page ceiling (`MAX_CVD_PAGE_LIMIT`). Asking past it is capped. */
export const CVD_PAGE_LIMIT = 300;

/** How close to the loaded edge a pan must come before the next page is worth fetching. */
const LEFT_EDGE_PREFETCH_BARS = 3;

export interface CvdPage {
  readonly bars: readonly Cvd2Bar[];
  readonly root: string;
  readonly servedKey: string;
  readonly hasMore: boolean;
  readonly nextBefore: number | null;
  readonly pageEmpty: boolean;
}

export type CvdPageRead =
  | { readonly page: CvdPage; readonly blocked: null }
  | { readonly page: null; readonly blocked: string };

/**
 * Sub-minute rungs read `/api/es/cvd/submin`, which is TICK-derived and has no cursor door —
 * it refuses `before=` by name. Offering to page there would produce a 400 on every pan.
 */
export function canPageDeepCvd(rung: Rung): boolean {
  return rung.kind === "tf";
}

export function cvdPageUrl(
  rung: Rung,
  limit: number,
  before: number | null = null,
): string {
  if (!Number.isFinite(limit)) throw new RangeError(`cvdPageUrl: limit must be finite, got ${limit}`);
  if (!canPageDeepCvd(rung)) {
    throw new RangeError("CVD cursor paging is a minute-rung route; sub-minute reads /cvd/submin");
  }
  const appliedLimit = Math.max(1, Math.min(CVD_PAGE_LIMIT, Math.floor(limit)));
  const query = new URLSearchParams({ tf: (rung as { tf: string }).tf, limit: String(appliedLimit) });
  if (before !== null) {
    if (!Number.isSafeInteger(before) || before < 946_684_800_000) {
      throw new RangeError(`before must be an integer epoch-ms cursor, got ${before}`);
    }
    // ⛔ GRID ALIGNMENT IS THE ROUTE'S CONTRACT AND IS NOT RE-IMPLEMENTED HERE. The Worker 400s a
    // mid-bucket cursor by name (`cvdBeforeAlignmentError`), and `readCvdPage` renders that
    // refusal loudly. A second copy of the rule in the client would be a SECOND CONSTRUCTION OF
    // ONE CONTRACT — the shape this repo has paid for repeatedly — and it would be the copy that
    // drifts: the server derives the grid from `date_bin` over the STORED bars, while a client
    // rule can only derive it from the rung it THINKS it asked for. `candlePaging.ts` made the
    // same call for the same reason. ⇒ ***ONE AUTHORITY PER RULE; THE OTHER SIDE REPORTS IT.***
    query.set("before", String(before));
  }
  return `/api/es/cvd?${query.toString()}`;
}

const finite = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * Read one served page, or REFUSE IT BY NAME.
 *
 * ⚠ THE RUNG AND ROOT CHECKS ARE NOT CEREMONY. `/api/es/cvd` once answered `tf=1/12/180/banana`
 * with one byte-identical series (the defect `TfSwitcher.svelte` still carries the note for);
 * a page reader that trusted the request rather than the ECHO would silently prepend 1m bars
 * under a 12m label, which draws as a chart whose history has the wrong shape.
 */
export function readCvdPage(body: unknown, requested: Rung, expectRoot = "ES"): CvdPageRead {
  if (body === null || typeof body !== "object") {
    return { page: null, blocked: `${rungLabel(requested)} CVD history returned an unreadable payload` };
  }
  const raw = body as Record<string, unknown>;
  if (raw.ok !== true) {
    return { page: null, blocked: `${rungLabel(requested)} CVD history refused: ${String(raw.reason ?? raw.error ?? "no reason given")}` };
  }
  if (raw.root !== expectRoot) {
    return { page: null, blocked: `CVD history served root=${String(raw.root)}, expected root=${expectRoot}; refusing to pool roots` };
  }
  if (!canPageDeepCvd(requested)) {
    return { page: null, blocked: `${rungLabel(requested)} has no CVD cursor door` };
  }
  const expectedTf = (requested as { tf: string }).tf;
  if (raw.tf_applied !== expectedTf) {
    return { page: null, blocked: `CVD history served rung ${String(raw.tf_applied)}, selected ${expectedTf}; refusing a mislabeled series` };
  }
  if (!Array.isArray(raw.bars)) {
    return { page: null, blocked: `${rungLabel(requested)} CVD history payload has no bars array` };
  }
  const bars: Cvd2Bar[] = [];
  for (const [index, value] of raw.bars.entries()) {
    if (value === null || typeof value !== "object") {
      return { page: null, blocked: `${rungLabel(requested)} CVD history bar ${index} is not an object` };
    }
    const row = value as Record<string, unknown>;
    if (!finite(row.t)) {
      return { page: null, blocked: `${rungLabel(requested)} CVD history bar ${index} has no finite t` };
    }
    // ⛔ THE WICK FIELDS ARE COPIED, NEVER COMPLETED. A legacy row's all-null candle is the
    // route's honest answer and 35-S2 renders it flat; substituting `cvd` for a missing `cvd_o`
    // here would paint a measured-looking body over an unmeasured bar.
    bars.push(value as Cvd2Bar);
  }
  const nextBefore = raw.next_before == null
    ? null
    : finite(raw.next_before) && Number.isSafeInteger(raw.next_before) ? raw.next_before : Number.NaN;
  if (Number.isNaN(nextBefore)) {
    return { page: null, blocked: `${rungLabel(requested)} CVD history served an invalid next_before cursor` };
  }
  const hasMore = raw.has_more === true;
  if (hasMore && nextBefore === null) {
    return { page: null, blocked: `${rungLabel(requested)} CVD history claims has_more without next_before` };
  }
  return {
    page: {
      bars,
      root: expectRoot,
      servedKey: rungKey(requested),
      hasMore,
      nextBefore,
      pageEmpty: raw.page_empty === true || bars.length === 0,
    },
    blocked: null,
  };
}

/** Prepend older bars, de-duplicating the seam. The CURRENT page wins any shared `t`. */
export function mergeCvdPages(
  older: readonly Cvd2Bar[],
  current: readonly Cvd2Bar[],
): Cvd2Bar[] {
  const byTime = new Map<number, Cvd2Bar>();
  for (const bar of older) byTime.set(bar.t, bar);
  for (const bar of current) byTime.set(bar.t, bar);
  return [...byTime.values()].sort((a, b) => a.t - b.t);
}

/**
 * The feed the pane consumes, with history in front of the live window.
 *
 * ⛔ THE ENVELOPE IS THE LIVE ONE. `age_s`, `stale` and `cvd_state` describe the NEWEST data the
 * panel holds, and a history page's own (legitimately five-months-old) age must never reach the
 * chip. Only `bars` grows. ⇒ ***A PREPEND EXTENDS THE SERIES, IT DOES NOT RE-DATE THE FEED.***
 */
export function withCvdHistory(live: Cvd2Feed | null, older: readonly Cvd2Bar[]): Cvd2Feed | null {
  if (live === null) return null;
  if (older.length === 0) return live;
  return { ...live, bars: mergeCvdPages(older, live.bars ?? []) };
}

/** The left-edge decision, shared in SHAPE with the candle rail but read off the CVD series. */
export function shouldLoadOlderCvd(
  window: { from: number; to: number } | null,
  oldestLoadedMs: number | null,
  rung: Rung,
): boolean {
  if (window === null || oldestLoadedMs === null || !canPageDeepCvd(rung)) return false;
  return window.from <= oldestLoadedMs + LEFT_EDGE_PREFETCH_BARS * rungSpanMs(rung);
}
